import React from "react";
import { Button, Alert } from "flowbite-react";
import { HiCheckCircle } from "react-icons/hi";
import { useDispatch, useSelector } from "react-redux";
import { getBillingPortalLink } from "../../api/stripe";
import { showNotification } from "../../actions/notification";
import dayjs from "dayjs";
import "dayjs/locale/pt-br";
import {
  ACTIVATION_REDIRECT_STORAGE_KEY,
  hasActiveSubscription,
  hasPlanAccess,
  hasStripeCustomer,
  isPaymentFailureIssue,
} from "../../utils/subscription";

dayjs.locale("pt-br");

const formatMoeda = (valor) =>
  `R$ ${Number(valor).toFixed(2).replace(".", ",")}`;

const recursosGratis = [
  "3 links ativos",
  "5 MB de armazenamento",
  "Cadastro dos primeiros produtos",
  "Acesso a página de ajuda",
  "Sem suporte via WhatsApp",
  "Sem análise de engajamento",
];

const recursosPagos = [
  "10 links ativos",
  "200 MB de armazenamento",
  "Suporte via WhatsApp",
  "Análise de engajamento no dashboard",
  "Portal de cobrança Stripe",
];

const planos = [
  {
    id: "gratis",
    nome: "Grátis",
    descricao: "Para criar sua vitrine inicial sem pagamento.",
    valor: 0,
    periodo: "/sempre",
    valorTotal: "Sem cobrança e sem Stripe.",
    destaqueTexto: "Plano inicial",
    recursos: recursosGratis,
  },
  {
    id: "mensal",
    nome: "Mensal",
    descricao: "Flexibilidade total. Cancele quando quiser.",
    valor: 35,
    periodo: "/mês",
    valorTotal: "Cobrança mensal de R$ 35,00",
    destaqueTexto: "Cancele quando quiser",
    recursos: recursosPagos,
    link: process.env.REACT_APP_PLAN_MENSAL_URL,
  },
  {
    id: "trimestral",
    nome: "Trimestral",
    descricao: "Mais previsibilidade sem compromisso longo.",
    valor: 33,
    periodo: "/mês",
    valorTotal: "Total de R$ 99,00 a cada 3 meses",
    destaqueTexto: "Plano equilibrado",
    recursos: recursosPagos,
    link: process.env.REACT_APP_PLAN_TRIMESTRAL_URL,
  },
  {
    id: "semestral",
    nome: "Semestral",
    descricao: "Bom custo-benefício para manter constância.",
    valor: 31,
    periodo: "/mês",
    valorTotal: "Total de R$ 186,00 a cada 6 meses",
    destaque: true,
    destaqueTexto: "Mais escolhido",
    recursos: recursosPagos,
    link: process.env.REACT_APP_PLAN_SEMESTRAL_URL,
  },
  {
    id: "anual",
    nome: "Anual",
    descricao: "Menor valor mensal para operações mais estáveis.",
    valor: 29,
    periodo: "/mês",
    valorTotal: "Total de R$ 348,00 por ano",
    destaqueTexto: "Melhor valor mensal",
    recursos: recursosPagos,
    link: process.env.REACT_APP_PLAN_ANUAL_URL,
  },
];

const Planos = () => {
  const dispatch = useDispatch();
  const profile = useSelector((state) => state.user.profile || null);
  const email = profile?.email || "";
  const planoAtivoStripe = profile?.planoAtivo || null;
  const subscriptionExpiresAt = profile?.subscriptionExpiresAt || null;
  const planoLocal = profile?.permissoesPlano?.plano || null;
  const codigoPlanoAtual = planoLocal?.codigo || null;
  const nomePlanoAtual = planoLocal?.nome || planoAtivoStripe || null;

  const assinaturaAtiva = hasActiveSubscription(profile);
  const acessoPlano = hasPlanAccess(profile);
  const hasStripe = hasStripeCustomer(profile);
  const pagamentoFalhou = isPaymentFailureIssue(profile);
  const planoGratisAtual = codigoPlanoAtual === "gratis";
  const planoMasterAtual = codigoPlanoAtual === "master";

  const dataExpiracao = assinaturaAtiva
    ? dayjs(subscriptionExpiresAt).format("DD [de] MMMM [de] YYYY")
    : null;

  const handlePagamento = (plano) => {
    if (plano.id === "gratis") {
      dispatch(
        showNotification({
          message: "O plano grátis é aplicado automaticamente ao criar a conta.",
          color: "info",
        })
      );
      return;
    }

    if (!plano.link) {
      dispatch(
        showNotification({
          message: "Link de pagamento não configurado para este plano.",
          color: "failure",
        })
      );
      return;
    }

    window.sessionStorage.setItem(ACTIVATION_REDIRECT_STORAGE_KEY, "1");
    window.location.href = `${plano.link}?prefilled_email=${encodeURIComponent(email)}`;
  };

  const handleBillingLink = async () => {
    try {
      const data = await getBillingPortalLink();
      if (data.url) {
        window.sessionStorage.setItem(ACTIVATION_REDIRECT_STORAGE_KEY, "1");
        window.location.href = data.url;
      } else {
        dispatch(
          showNotification({
            message: "Erro ao obter o link do portal de faturamento.",
            color: "failure",
          })
        );
      }
    } catch (error) {
      dispatch(
        showNotification({
          message: error.message,
          color: "failure",
        })
      );
    }
  };

  const titulo = acessoPlano
    ? "Seu plano"
    : pagamentoFalhou
    ? "Pagamento não aprovado"
    : "Escolha seu plano";

  return (
    <div className="flex flex-col items-center justify-center min-h-screen p-6">
      <h1 className="text-4xl font-bold text-gray-800 mb-4 text-center">{titulo}</h1>

      {acessoPlano ? (
        <div className="w-full max-w-4xl space-y-3 mb-8">
          <Alert color={assinaturaAtiva ? "success" : "info"}>
            Você está no plano <b>{nomePlanoAtual || "ativo"}</b>
            {assinaturaAtiva && dataExpiracao
              ? `, com renovação automática prevista para ${dataExpiracao}.`
              : "."}
          </Alert>
          {planoGratisAtual ? (
            <Alert color="info">
              O plano grátis permite começar sem pagamento. Faça upgrade quando
              precisar de mais links, espaço, suporte ou análises.
            </Alert>
          ) : null}
          {planoMasterAtual ? (
            <Alert color="success">
              O plano master é local, não depende do Stripe e libera links e
              armazenamento ilimitados.
            </Alert>
          ) : null}
        </div>
      ) : null}

      {!acessoPlano ? (
        <div className="w-full max-w-4xl space-y-3 mb-8">
          <Alert color={pagamentoFalhou ? "warning" : "info"}>
            {pagamentoFalhou
              ? "Não foi possível processar o último pagamento. Atualize seu método de pagamento ou escolha um plano para reativar."
              : "Crie sua vitrine grátis ou escolha um plano pago quando precisar de mais capacidade."}
          </Alert>
          {pagamentoFalhou && profile?.subscriptionIssueMessage ? (
            <Alert color="info">{profile.subscriptionIssueMessage}</Alert>
          ) : null}
        </div>
      ) : null}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-5 w-full max-w-7xl">
        {planos.map((plano) => {
          const planoAtual = codigoPlanoAtual === plano.id;
          const cobrarPeloPortal = assinaturaAtiva && hasStripe && plano.id !== "gratis";
          const bloqueadoPorMaster = planoMasterAtual;
          const disabled = planoAtual || bloqueadoPorMaster;

          const textoBotao = planoAtual
            ? "Plano atual"
            : bloqueadoPorMaster
            ? "Plano master ativo"
            : cobrarPeloPortal
            ? "Gerenciar no portal"
            : plano.id === "gratis"
            ? "Incluído ao cadastrar"
            : pagamentoFalhou
            ? `Reativar no ${plano.nome}`
            : `Assinar ${plano.nome}`;

          return (
            <div
              key={plano.id}
              className={`relative flex flex-col justify-between rounded-lg border bg-white p-5 shadow-md ${
                plano.destaque
                  ? "border-purple-600 shadow-lg ring-1 ring-purple-100"
                  : "border-gray-200"
              } ${disabled ? "opacity-70" : ""}`}
            >
              {plano.destaque ? (
                <div className="absolute -top-3 left-5 rounded-full bg-purple-700 px-3 py-1 text-xs font-semibold text-white shadow-sm">
                  Recomendado
                </div>
              ) : null}

              <div>
                <h2 className="text-2xl font-semibold text-gray-800 text-center">
                  {plano.nome}
                </h2>
                <p className="min-h-[3rem] text-gray-600 text-center text-sm mt-2">
                  {plano.descricao}
                </p>
              </div>

              <div className="text-left mt-5">
                <div className="flex items-baseline justify-center gap-2 flex-wrap">
                  <span className="text-3xl font-bold tracking-tight text-purple-700">
                    {formatMoeda(plano.valor)}
                  </span>
                  <span className="text-sm font-medium text-gray-500">
                    {plano.periodo}
                  </span>
                </div>
                <p className="min-h-[2.5rem] text-gray-500 text-sm mt-2 text-center">
                  {plano.valorTotal}
                </p>
              </div>

              <ul className="mt-5 space-y-3 text-sm text-gray-700">
                {plano.recursos.map((recurso) => (
                  <li key={recurso} className="flex gap-2">
                    <HiCheckCircle className="mt-0.5 h-4 w-4 flex-none text-purple-600" />
                    <span>{recurso}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-4 flex justify-center">
                <div className="inline-flex items-center rounded-full bg-purple-50 px-3 py-1 text-xs font-medium text-purple-700 text-center">
                  {plano.destaqueTexto}
                </div>
              </div>

              <Button
                gradientDuoTone="purpleToPink"
                className="mt-6 w-full"
                disabled={disabled}
                onClick={() =>
                  cobrarPeloPortal ? handleBillingLink() : handlePagamento(plano)
                }
              >
                {textoBotao}
              </Button>
            </div>
          );
        })}
      </div>

      {hasStripe ? (
        <Button
          gradientDuoTone="pinkToOrange"
          className="mt-8 w-64"
          onClick={handleBillingLink}
        >
          Gerenciar assinatura
        </Button>
      ) : null}
    </div>
  );
};

export default Planos;
